import { ExportDownloadOut } from "../utils/downloadExport";
import { postDataV2 } from "./utils";

// Live on the feat-2/cedar-restructure backend branch (same branch name as this frontend's own) -
// a genuinely generic wrapper, not a per-dashboard endpoint: AnalysisService looks up
// ANALYSIS_REGISTRY by the dashboard's `key` (services/analysis/registry.py) to find its filter
// Pydantic schema/options function/export specs, reusing each dashboard's already-existing
// filter/export code rather than duplicating it.
//
// Only dashboards actually present in ANALYSIS_REGISTRY 404 as "not registered for analysis".
// This registry has already changed once since first confirmed (a real commit added `ecommerce`/
// `product_performance`/`product_performance_deep_dive`/`cpo_operational` and renamed one of
// `dis`'s own export names), so this file deliberately doesn't hand-maintain a static list of
// which dashboards/exports exist - see exportDashboardAnalysis's own comment for how the frontend
// discovers a dashboard's real export names instead of guessing them.

// Each entry's exact shape isn't a fixed schema (the backend types it as a loose
// `List[Dict[str, Any]]`, generated per-dashboard by `describe_filters()`) - typed defensively
// here as "at least a key" with everything else optional/unknown, rather than assuming every
// dashboard's filter fields all carry the same extra properties.
//
// `options` is confirmed (against a real DIS response) as a bare array of strings for most
// dashboards, not `{label, value}` objects - but CPO Operational's own `year` field comes back as
// bare *numbers* instead (a real `List[int]` backend field, not `List[str]` - see
// ApplyFiltersTile.tsx's own normalizeOption comment). The union stays defensive for the
// `{label, value}` shape too, in case some other dashboard's options ever come back richer still.
export interface AnalysisFilterField {
  key: string;
  label?: string;
  type?: string;
  options?: Array<string | number | { label: string; value: string }>;
  default?: string | null;
  [extra: string]: unknown;
}

// Real per-dashboard export names, already resolved server-side (services/analysis/service.py's
// get_dashboard_filters) - AnalyticsPage.tsx used to only ever learn these reactively, from a
// failed /export call's own error message, meaning the very first Download click for a
// multi-export dashboard always 400'd once before anything downloaded. Optional/possibly-absent
// like `filters` itself (AnalysisFilterField's own comment) - not assumed to always be present or
// shaped exactly like this.
export interface AnalysisExportDescriptor {
  key: string;
  label: string;
}

// Real per-dashboard page names, already resolved server-side (services/analysis/service.py's
// get_dashboard_filters) for a dashboard registered with more than one named "page" (e.g.
// brand_performance's brand_volume/sov_som/purchase_funnel/...) - undefined/empty for every
// single-page dashboard. `page` is the backend's own chosen default (the first registered page)
// whenever the caller didn't specify one, so a fresh multi-page dashboard already has a valid
// selection to show/submit before the user picks anything themselves.
export interface AnalysisPageDescriptor {
  key: string;
  label: string;
}

export interface AnalysisFiltersResponse {
  dashboard_id: number;
  page?: string | null;
  pages?: AnalysisPageDescriptor[];
  filters: AnalysisFilterField[];
  exports?: AnalysisExportDescriptor[];
}

export interface AnalysisFiltersRequestOptions {
  // Only meaningful for multi-page dashboards (brand_performance) - omit for single-page ones.
  page?: string;
  use_cache?: boolean;
  // Only meaningful for brand_performance's pages - harmless default elsewhere since the backend
  // only reads it where relevant.
  nissan_competitor?: boolean;
}

// filters is the user's current in-progress selections (or {} on first load) - the backend
// re-derives each field's dynamic `options` from them (e.g. narrowing a model list once a market
// is picked), not just returning a static schema once.
export const getDashboardAnalysisFilters = (
  dashboardId: number,
  filters: Record<string, unknown> = {},
  options: AnalysisFiltersRequestOptions = {}
): Promise<AnalysisFiltersResponse> => {
  const params = new URLSearchParams();
  if (options.use_cache !== undefined) params.set("use_cache", String(options.use_cache));
  if (options.nissan_competitor !== undefined) params.set("nissan_competitor", String(options.nissan_competitor));
  const query = params.toString();

  // AnalysisFiltersRequest{page, filters} is FastAPI's sole request-body model here (not
  // embedded under its own key) - page belongs in the JSON body alongside filters, not the query
  // string. use_cache/nissan_competitor stay query params (plain scalar route params on the
  // backend, confirmed against a real DIS response).
  return postDataV2(
    `/analysis/${dashboardId}/filters${query ? `?${query}` : ""}`,
    { page: options.page, filters },
    `analysis-filters-${dashboardId}`
  );
};

// ExportOptions (schemas/analysis.py) bundles every dashboard's own export flags into one shared
// bag of optional booleans - a dashboard's export function only reads the ones relevant to it and
// ignores the rest. Not every field is meaningful for every dashboard; harmless to send defaults
// for fields a given dashboard's export doesn't use.
export interface AnalysisExportOptions {
  page?: string;
  // Required whenever a dashboard/page has more than one named export (e.g. dis's
  // "overall"/"by_car_model") - the backend 400s asking for it if omitted and ambiguous, and
  // silently picks the only one if there's just one.
  export?: string;
  use_cache?: boolean;
  nissan_competitor?: boolean;
  proportion?: boolean;
  digitally_influenced_sales_cumulative?: boolean;
  private_sales_breakdown_cumulative?: boolean;
  digital_leads_by_source_digitally_influenced_leads?: boolean;
  sales_breakdown_by_source_digitally_influenced_sales?: boolean;
}

// There's no generic "list available exports for this dashboard" endpoint - the backend only
// reveals a dashboard's real export names in a 400's own error message, when `export` is omitted
// and there's more than one option (e.g. "Query param 'export' is required for this dashboard
// page. Options: ['channel_mix', 'overall']"). A hand-maintained per-dashboard list of these names
// was tried first and already drifted out of sync with a real backend registry change (dis's own
// "by_car_model" export was renamed to "channel_mix" - the hardcoded list still said the old
// name), so AnalyticsPage.tsx instead parses this exact error message to discover the real options
// on demand (see its own findExportOptions) rather than guessing/maintaining them here.
//
// NS-908: this (and every export/*-csv-download endpoint) no longer streams the file directly -
// it uploads it to GCS and returns an ExportDownloadOut (download_url/file_name/...) for the
// client to fetch from instead. No more `"blob"` responseType - the response is now a plain JSON
// object either way (a real export on success, {error, status} on failure, exactly like any other
// postDataV2 call - see utils/downloadExport.ts's own comment on why that matters for error
// handling here).
export const exportDashboardAnalysis = (
  dashboardId: number,
  filters: Record<string, unknown> = {},
  options: AnalysisExportOptions = {}
): Promise<ExportDownloadOut> => {
  // AnalysisExportRequest{page, export, filters} is FastAPI's sole request-body model on this
  // route (not embedded under its own key) - all three belong in the JSON body together. Only
  // use_cache and the ExportOptions dependency's fields (nissan_competitor, proportion, ...) are
  // query params (plain route params on the backend).
  const { page, export: exportName, ...queryOptions } = options;
  const params = new URLSearchParams();
  Object.entries(queryOptions).forEach(([key, value]) => {
    if (value !== undefined) params.set(key, String(value));
  });
  const query = params.toString();

  return postDataV2(
    `/analysis/${dashboardId}/export${query ? `?${query}` : ""}`,
    { page, export: exportName, filters },
    `analysis-export-${dashboardId}`
  );
};
